import {
  STYLE_GUIDE_PROSE_SECTIONS,
  type StyleGuideProseSection,
} from '@/lib/brand/style-guide-markdown'
import type { SupportedLocale } from '@/i18n'
import { type BrandDocMeta, DRAFT_BRAND_DOC_META } from '@/types/brand-doc-meta'
import type { BrandFactsUpdate } from '@/types/brand-facts'
import type { BeliefQuestionKey } from '@/types/content-strategy-run'
import type { CustomerProfileUpdate } from '@/types/customer-profile'

/** Onboarding progress derived server-side from artifact existence; a signal, never a gate. */
export type OnboardingStatus = 'pending' | 'collecting-inputs' | 'complete'

export const BRAND_MARKET_SOURCES = ['language-default', 'overview', 'user'] as const

type BrandMarketSource = (typeof BRAND_MARKET_SOURCES)[number]

export interface BrandMarketCandidate {
  readonly locationCode: number
  readonly countryCode: string
  readonly estimatedTraffic: number | null
  readonly rankingKeywordCount: number | null
}

export interface BrandMarket {
  readonly locationCode: number
  readonly languageCode: string
  readonly source: BrandMarketSource
  readonly candidates: readonly BrandMarketCandidate[]
  readonly decidedAt: string
}

export interface SetBrandMarketInput {
  readonly locationCode: number
  readonly languageCode: string
}

/** The person who signs the brand's articles. */
export interface BrandAuthor {
  readonly name: string
  readonly role: string
  readonly bio: string
  readonly profileUrl: string
}

/** One edit to the brand's publishing details; an absent key stays as it is. */
export interface SaveBrandPublishingInput {
  readonly author?: BrandAuthor
  readonly trialUrl?: string | null
}

export interface Brand {
  readonly id: string
  readonly name: string
  /** IANA zone the brand's publishing times are read in; readers fall back to UTC. */
  readonly timeZone?: string
  readonly contentLanguage?: SupportedLocale
  readonly onboardingStatus: OnboardingStatus
  readonly competitorAnalysisComparingAt: string | null
  readonly competitorsConfirmedAt: string | null
  /** When the Visual style step was last confirmed; `null` until then. */
  readonly visualStyleConfirmedAt: string | null
  readonly market?: BrandMarket
  readonly searchSnapshotRunningAt?: string
  /** Signs every article; `null` until the founder fills it in. */
  readonly author: BrandAuthor | null
  /** Where a page sends a reader who is ready; `null` until set. */
  readonly trialUrl: string | null
  readonly createdAt: string | null
  readonly updatedAt: string | null
}

export interface ArtifactMeta {
  readonly updatedAt: string | null
}

export const JOURNEY_PHASES = ['awareness', 'consideration', 'decision'] as const

export type JourneyPhase = (typeof JOURNEY_PHASES)[number]

export const BELIEF_BASES = ['proposed', 'edited'] as const

export type BeliefBasis = (typeof BELIEF_BASES)[number]

export const CORE_MESSAGE_BASES = ['proposed', 'edited'] as const

export type CoreMessageBasis = (typeof CORE_MESSAGE_BASES)[number]

export interface Belief {
  readonly slug: string
  readonly phase: JourneyPhase
  readonly name: string
  readonly claim: string
  readonly objection: string
  readonly questionKey: BeliefQuestionKey | null
  readonly basis: BeliefBasis
}

export interface BeliefMap {
  readonly meta: BrandDocMeta
  readonly obviousChoiceBelief: string
  readonly obviousChoiceBeliefBasis: CoreMessageBasis
  /** The view the core message replaces; `''` when none. */
  readonly obviousChoiceBeliefReplaces: string
  readonly beliefs: readonly Belief[]
}

export const EMPTY_BELIEF_MAP: BeliefMap = {
  meta: DRAFT_BRAND_DOC_META,
  obviousChoiceBelief: '',
  obviousChoiceBeliefBasis: 'proposed',
  obviousChoiceBeliefReplaces: '',
  beliefs: [],
}

export interface EditableBelief {
  readonly phase: JourneyPhase
  readonly name: string
  readonly claim: string
  readonly objection: string
}

export type BeliefMapUpdate =
  | {
      readonly section: 'roof'
      readonly obviousChoiceBelief: string
      readonly obviousChoiceBeliefReplaces: string
    }
  | { readonly section: 'belief'; readonly mode: 'add'; readonly belief: EditableBelief }
  | {
      readonly section: 'belief'
      readonly mode: 'replace'
      readonly beliefSlug: string
      readonly belief: EditableBelief
    }
  | { readonly section: 'belief'; readonly mode: 'remove'; readonly beliefSlug: string }

export const POSTING_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

export type PostingDay = (typeof POSTING_DAYS)[number]

export interface PostingCadence {
  readonly postsPerWeek: number
  readonly preferredDays?: readonly PostingDay[]
  readonly preferredTimes?: Partial<Record<PostingDay, string>>
}

export type PhaseWeights = Readonly<Record<JourneyPhase, number>>

export const DEFAULT_PHASE_WEIGHTS: PhaseWeights = {
  awareness: 50,
  consideration: 30,
  decision: 20,
}

export interface ContentStrategy {
  readonly meta: BrandDocMeta
  readonly postingCadence?: PostingCadence
  readonly phaseWeights: PhaseWeights
}

export interface VisualStyle {
  readonly meta: ArtifactMeta
  readonly colors: readonly string[]
  readonly fontFamily: string | null
  readonly logo: VisualStyleAsset | null
  readonly monogram: VisualStyleAsset | null
}

export interface VisualStyleAsset {
  readonly name: string
  readonly src: string
}

export interface StyleGuideTerm {
  readonly term: string
  readonly correctForm: string
}

export interface StyleGuidePreference {
  readonly avoid: string
  readonly useInstead: string
}

export interface StyleGuideSample {
  readonly body: string
  readonly sourceUrl: string | null
  readonly publishedAt: string | null
}

/** `styleGuide/current` - the conventions the brand's writing already follows; `sampleCopy` is never edited here. */
export interface StyleGuide {
  readonly meta: ArtifactMeta
  readonly voiceAndPersonality: string
  readonly formalityAndAddress: string
  readonly referenceGrammarStandard: string
  readonly numbersDatesCurrency: string
  readonly punctuationAndQuotes: string
  readonly capitalization: string
  readonly brandAndProductTerms: readonly StyleGuideTerm[]
  readonly preferredAndAvoidedWords: readonly StyleGuidePreference[]
  readonly inclusiveLanguage: string
  readonly sampleCopy: readonly StyleGuideSample[]
}

export interface ContentStrategyUpdate {
  readonly section: 'cadence'
  readonly cadence: {
    readonly postsPerWeek: number
    readonly preferredDays: readonly PostingDay[]
    readonly preferredTimes?: Readonly<Record<string, string>>
  }
}

export interface VisualStyleUpdate {
  readonly colors?: readonly string[]
  readonly fontFamily?: string
  readonly logo?: VisualStyleAsset | null
  readonly monogram?: VisualStyleAsset | null
}

export type StyleGuideUpdate =
  | { readonly section: StyleGuideProseSection; readonly body: string }
  | {
      readonly section: 'brandAndProductTerms'
      readonly mode: 'add'
      readonly term: StyleGuideTerm
    }
  | {
      readonly section: 'brandAndProductTerms'
      readonly mode: 'replace'
      readonly index: number
      readonly term: StyleGuideTerm
    }
  | { readonly section: 'brandAndProductTerms'; readonly mode: 'remove'; readonly index: number }
  | {
      readonly section: 'preferredAndAvoidedWords'
      readonly mode: 'add'
      readonly preference: StyleGuidePreference
    }
  | {
      readonly section: 'preferredAndAvoidedWords'
      readonly mode: 'replace'
      readonly index: number
      readonly preference: StyleGuidePreference
    }
  | {
      readonly section: 'preferredAndAvoidedWords'
      readonly mode: 'remove'
      readonly index: number
    }

export type UpdateBrandDocumentPayload =
  | { readonly slug: 'brand-facts'; readonly data: BrandFactsUpdate }
  | { readonly slug: 'content-strategy'; readonly data: ContentStrategyUpdate }
  | { readonly slug: 'belief-map'; readonly data: BeliefMapUpdate }
  | { readonly slug: 'style-guide'; readonly data: StyleGuideUpdate }
  | { readonly slug: 'customer-profile'; readonly data: CustomerProfileUpdate }
  | { readonly slug: 'visual-style'; readonly data: VisualStyleUpdate }

export function phaseOfQuestion(key: BeliefQuestionKey): JourneyPhase {
  const [phase] = key.split('.') as [JourneyPhase]
  return phase
}

export function hasBeliefMapContent(map: BeliefMap): boolean {
  return map.obviousChoiceBelief.trim() !== '' && map.beliefs.length > 0
}

export function hasStyleGuideContent(guide: StyleGuide): boolean {
  return (
    STYLE_GUIDE_PROSE_SECTIONS.some((section) => guide[section].trim() !== '') ||
    guide.brandAndProductTerms.length > 0 ||
    guide.preferredAndAvoidedWords.length > 0
  )
}

/** Turns an edited list into positional edits in the order the server can apply them. */
function listUpdates<T, U>(
  current: readonly T[],
  next: readonly T[],
  same: (a: T, b: T) => boolean,
  replace: (index: number, entry: T) => U,
  remove: (index: number) => U,
  add: (entry: T) => U,
): U[] {
  const updates: U[] = []

  const shared = Math.min(current.length, next.length)
  for (let index = 0; index < shared; index += 1) {
    const before = current[index]
    const after = next[index]
    if (before && after && !same(before, after)) updates.push(replace(index, after))
  }

  for (let index = current.length - 1; index >= next.length; index -= 1) {
    updates.push(remove(index))
  }

  for (const entry of next.slice(current.length)) updates.push(add(entry))

  return updates
}

/** Turns an edited Style Guide into per-section edits; an emptied prose section is not sent. */
export function styleGuideUpdates(current: StyleGuide, next: StyleGuide): StyleGuideUpdate[] {
  const updates: StyleGuideUpdate[] = []

  for (const section of STYLE_GUIDE_PROSE_SECTIONS) {
    const body = next[section]
    if (body.trim() !== '' && body !== current[section]) updates.push({ section, body })
  }

  updates.push(
    ...listUpdates<StyleGuideTerm, StyleGuideUpdate>(
      current.brandAndProductTerms,
      next.brandAndProductTerms,
      (a, b) => a.term === b.term && a.correctForm === b.correctForm,
      (index, term) => ({ section: 'brandAndProductTerms', mode: 'replace', index, term }),
      (index) => ({ section: 'brandAndProductTerms', mode: 'remove', index }),
      (term) => ({ section: 'brandAndProductTerms', mode: 'add', term }),
    ),
  )

  updates.push(
    ...listUpdates<StyleGuidePreference, StyleGuideUpdate>(
      current.preferredAndAvoidedWords,
      next.preferredAndAvoidedWords,
      (a, b) => a.avoid === b.avoid && a.useInstead === b.useInstead,
      (index, preference) => ({
        section: 'preferredAndAvoidedWords',
        mode: 'replace',
        index,
        preference,
      }),
      (index) => ({ section: 'preferredAndAvoidedWords', mode: 'remove', index }),
      (preference) => ({ section: 'preferredAndAvoidedWords', mode: 'add', preference }),
    ),
  )

  return updates
}
